// src/pages/Organizations/Marketing/OrganizationLogoManager.tsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Star as StarIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { getStorage, ref, uploadBytes, getDownloadURL, listAll } from 'firebase/storage';
import { collection, addDoc, updateDoc, doc, serverTimestamp, getDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';

interface Logo {
  id: string;
  organizationId: string;
  url: string;
  name: string;
  purpose: string;
  dimensions: {
    width: number;
    height: number;
  };
  fileType: string;
  isDefault: boolean;
  createdAt: any;
  updatedAt: any;
}

interface OrganizationLogoManagerProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
}

const OrganizationLogoManager: React.FC<OrganizationLogoManagerProps> = ({
  open,
  onClose,
  organizationId
}) => {
  const [logos, setLogos] = useState<Logo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  
  // For logo upload
  const [logoName, setLogoName] = useState('');
  const [logoPurpose, setLogoPurpose] = useState('primary');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  
  // Ref for file input
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Fetch logos when component mounts or organizationId changes
  useEffect(() => {
    if (open && organizationId) {
      fetchLogos();
    }
  }, [open, organizationId]);
  
  const fetchLogos = async () => {
    if (!organizationId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const logosQuery = query(
        collection(db, 'organizationLogos'),
        where('organizationId', '==', organizationId)
      );
      
      const snapshot = await getDocs(logosQuery);
      const fetchedLogos: Logo[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Logo[];
      
      setLogos(fetchedLogos);
    } catch (err) {
      console.error('Error fetching logos:', err);
      setError('Failed to load organization logos');
    } finally {
      setLoading(false);
    }
  };
  
  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    const file = e.target.files[0];
    
    setUploadedFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewUrl(reader.result as string);
      
      // Load image to get dimensions
      const img = new Image();
      img.onload = () => {
        // Set default name based on dimensions if not set
        if (!logoName) {
          setLogoName(`Organization Logo ${img.width}x${img.height}`);
        }
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  };
  
  const handleUploadLogo = async () => {
    if (!uploadedFile || !logoName || !organizationId) {
      setError('Please provide all required information');
      return;
    }
    
    setUploading(true);
    setError(null);
    
    try {
      // Get image dimensions from preview
      let dimensions = { width: 0, height: 0 };
      if (previewUrl) {
        const img = new Image();
        img.src = previewUrl;
        dimensions = {
          width: img.width || 0,
          height: img.height || 0
        };
      }
      
      // Upload to Firebase Storage
      const storage = getStorage();
      const logoFileName = `logo_${Date.now()}_${uploadedFile.name}`;
      const storageRef = ref(storage, `organizations/${organizationId}/logos/${logoFileName}`);
      
      await uploadBytes(storageRef, uploadedFile);
      const downloadUrl = await getDownloadURL(storageRef);
      
      // Save metadata to Firestore
      const logoData: Omit<Logo, 'id'> = {
        organizationId,
        url: downloadUrl,
        name: logoName,
        purpose: logoPurpose,
        dimensions,
        fileType: uploadedFile.type,
        isDefault: logos.length === 0, // Make default if it's the first logo
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const logoDocRef = await addDoc(collection(db, 'organizationLogos'), logoData);
      
      // Add to local state
      setLogos([...logos, { 
        ...logoData, 
        id: logoDocRef.id,
        createdAt: new Date(),
        updatedAt: new Date()
      }]);
      
      // Reset form
      setUploadedFile(null);
      setPreviewUrl(null);
      setLogoName('');
      setLogoPurpose('primary');
      
      // Close dialog
      setUploadDialogOpen(false);
      
      setSuccess('Logo uploaded successfully');
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError('Failed to upload logo. Please try again.');
    } finally {
      setUploading(false);
    }
  };
  
  const handleSetDefault = async (logoId: string) => {
    try {
      setLoading(true);
      
      // Update in Firestore - first reset all logos
      const logoCollectionRef = collection(db, 'organizationLogos');
      const currentLogos = logos.filter(logo => logo.organizationId === organizationId);
      
      for (const logo of currentLogos) {
        if (logo.isDefault) {
          await updateDoc(doc(logoCollectionRef, logo.id), {
            isDefault: false,
            updatedAt: serverTimestamp()
          });
        }
      }
      
      // Set the selected logo as default
      await updateDoc(doc(logoCollectionRef, logoId), {
        isDefault: true,
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      setLogos(logos.map(logo => ({
        ...logo,
        isDefault: logo.id === logoId
      })));
      
      setSuccess('Default logo updated');
    } catch (err) {
      console.error('Error setting default logo:', err);
      setError('Failed to set default logo');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Organization Logo Management</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          
          <Box sx={{ mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => setUploadDialogOpen(true)}
            >
              Upload New Logo
            </Button>
          </Box>
          
          <Typography variant="subtitle1" gutterBottom>
            Available Logos
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : logos.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <UploadIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
              <Typography variant="h6" gutterBottom>No Logos Available</Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Upload your organization logos to use them in marketing materials
              </Typography>
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={() => setUploadDialogOpen(true)}
              >
                Upload First Logo
              </Button>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {logos.map(logo => (
                <Grid item xs={12} sm={6} md={4} key={logo.id}>
                  <Card>
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="140"
                        image={logo.url}
                        alt={logo.name}
                        sx={{ 
                          objectFit: 'contain',
                          bgcolor: 'rgba(0,0,0,0.03)'
                        }}
                      />
                      {logo.isDefault && (
                        <Chip 
                          label="Default Logo" 
                          color="primary" 
                          size="small"
                          icon={<StarIcon />}
                          sx={{ 
                            position: 'absolute', 
                            top: 8, 
                            right: 8 
                          }}
                        />
                      )}
                    </Box>
                    <CardContent>
                      <Typography variant="subtitle1">{logo.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {logo.dimensions.width} x {logo.dimensions.height} px
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Purpose: {logo.purpose}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      {!logo.isDefault && (
                        <Button 
                          size="small" 
                          startIcon={<StarIcon />}
                          onClick={() => handleSetDefault(logo.id)}
                        >
                          Set as Default
                        </Button>
                      )}
                      <IconButton size="small">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Logo Upload Dialog */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Organization Logo</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Please upload a high-resolution logo with transparency for best results in marketing materials.
          </Typography>
          
          <Box sx={{ mb: 3, mt: 2 }}>
            {previewUrl ? (
              <Box sx={{ position: 'relative' }}>
                <img
                  src={previewUrl}
                  alt="Logo Preview"
                  style={{ 
                    width: '100%', 
                    height: 200, 
                    objectFit: 'contain',
                    backgroundColor: 'rgba(0,0,0,0.03)',
                    borderRadius: 4
                  }}
                />
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                  onClick={() => {
                    setUploadedFile(null);
                    setPreviewUrl(null);
                  }}
                >
                  Remove
                </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: 200,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed rgba(0,0,0,0.2)',
                  borderRadius: 2,
                  backgroundColor: 'rgba(0,0,0,0.03)',
                  cursor: 'pointer'
                }}
                onClick={handleFileSelect}
              >
                <UploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography>Click to select a logo file</Typography>
              </Box>
            )}
          </Box>
          
          <input
            type="file"
            accept="image/*"
            hidden
            ref={fileInputRef}
            onChange={handleFileChange}
          />
          
          <TextField
            label="Logo Name"
            value={logoName}
            onChange={(e) => setLogoName(e.target.value)}
            fullWidth
            margin="normal"
            required
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Logo Purpose</InputLabel>
            <Select
              value={logoPurpose}
              label="Logo Purpose"
              onChange={(e) => setLogoPurpose(e.target.value)}
            >
              <MenuItem value="primary">Primary Logo</MenuItem>
              <MenuItem value="secondary">Secondary Logo</MenuItem>
              <MenuItem value="monochrome">Monochrome Logo</MenuItem>
              <MenuItem value="watermark">Watermark</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={handleUploadLogo}
            disabled={!uploadedFile || !logoName || uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : null}
          >
            {uploading ? 'Uploading...' : 'Upload Logo'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrganizationLogoManager;